import React, { useEffect, useState } from 'react'
import { DrawerItemProps } from '@progress/kendo-react-layout'
import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import { BookingForm } from 'types/booking'
import { OrderType } from 'types/order'
import { TenantIntegration } from 'types/tenant-integration'
import { useAppointmentDrawerContext } from 'context/appointmentDrawerContext/AppointmentDrawerContainer'
import AppointmentForm from './AppointmentForm'
import AddAttendeeModal from './AddAttendeeModal'
import AddNewClient from './AddNewClient'
import AlertPrompt from 'components/AlertPrompt'
import { classNames } from 'utils/dom-helpers'
import { Popup } from '@progress/kendo-react-popup'
import { useTenantContext } from 'context/TenantContext'
import { useRouter } from 'next/router'
import { useTenantIntegrationService } from 'services/tenant-integration'

export interface IAppointmentDrawer extends DrawerItemProps {
    onClose?: () => void
}

const AppointmentDrawer: React.FC<IAppointmentDrawer> = (props) => {
    const { getLocationPaymentIntegrations } = useTenantIntegrationService()
    const router = useRouter()
    const {
        formData,
        displayAddAttendeeModal,
        setDisplayAddAttendeeModal,
        displayNewClientForm,
        err,
        setErrMsg,
        isSuccess,
    } = useAppointmentDrawerContext()
    const { tenant, payment_integrations: tenant_payment_integrations } = useTenantContext()
    const { translations } = useAppContext()

    const [totalSlots, setTotalSlots] = useState(+formData?.attendees)
    const [popup_anim_css, setPopAmin] = useState<string>('bg-opacity-50')

    const [payment_integrations, setPaymentIntegrations] =
        useState<TenantIntegration[]>()
    const [booking_form, setBookingForm] = useState<BookingForm>({
        first_name: '',
        timezone: '',
        order_type: OrderType.MEMBERSHIP,
        amount: 10,
    })

    useEffect(() => {
        if (formData.location) {
            getLocationPaymentIntegrations(formData.location)
                .then((integrations) => {
                    if (integrations && integrations.length > 0) {
                        setPaymentIntegrations(integrations)
                    } else if (tenant?.id) {
                        setPaymentIntegrations(tenant_payment_integrations)
                    }
                })
                .catch(console.error)
        }
    }, [formData.location])

    useEffect(() => {
        if (payment_integrations && payment_integrations.length > 0) {
            payment_integrations.forEach(
                (integration: TenantIntegration) => {
                    if (!booking_form.connected_account_id) {
                        booking_form.connected_account_id =
                            integration.connected_account_id
                        setBookingForm({
                            ...booking_form,
                            connected_account_id:
                                integration.connected_account_id,
                        })
                    }
                }
            )
        }
    }, [payment_integrations])

    useEffect(() => {
        setTotalSlots(+formData?.attendees)
    }, [formData?.attendees])

    useEffect(() => {
        if (true) setPopAmin('bg-opacity-50')
        else setPopAmin('bg-opacity-0')
    }, [])

    const getHeader = () => (
        <div className="flex justify-between cursor-pointer">
            <div className="w-5/6">
                <h1 className="text-primary text-2xl font-semibold">
                    <Translation content_key="new_appointment" />
                </h1>
            </div>
            <div
                className="flex justify-end w-1/4"
                onClick={() => props.onClose && props.onClose()}
            >
                X
            </div>
            <div>
                <hr />
            </div>
        </div>
    )

    const enableAddAttendee = () => setDisplayAddAttendeeModal(true)

    const renderAttendeesSection = () => {
        return (
            <div className="h-32">
                <div className="flex justify-between items-center mt-4">
                    <Translation
                        render_as="div"
                        className="text-xl font-medium font-sans"
                        content_key="attendees"
                        translations={translations}
                    />
                    <div>
                        <small className="font-semibold tracking-wider">
                            {0}/1
                        </small>
                    </div>
                </div>
                <div className="flex gap-6 items-center py-3">
                    <div className="pb-0">
                        <a
                            href="#addAttendeeModalId"
                            className="border border-gray-400 px-3 h-10 rounded-lg text-primary items-center flex gap-1"
                            type="button"
                            onClick={() => enableAddAttendee()}
                        >
                            <i className="feather-plus text-xl" />
                            <Translation
                                render_as="span"
                                content_key="add_attendee"
                                translations={translations}
                            />
                        </a>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="h-full overflow-x-hidden flex flex-col transition ease-in-out duration-200">
            <div className="flex flex-col h-screen">
                <div className="p-4">{getHeader()}</div>

                <div>
                    <div className="p-4 pt-0">
                        <div className="bg-gray-100 rounded-md p-4">
                            <AppointmentForm />
                        </div>
                    </div>
                    <div className="mt-4">
                        <hr />
                    </div>

                    {err.length > 0 && (
                        <Popup
                            show={!!err.length}
                            popupAlign={{
                                horizontal: 'right',
                                vertical: 'top',
                            }}
                            animate={false}
                            popupClass={classNames(
                                'bg-black h-screen transition ease-in-out duration-200 flex flex-row items-center justify-between',
                                popup_anim_css
                            )}
                            style={{
                                width: '100%',
                            }}
                            className="z-[102]"
                        >
                            <div className="flex-1">
                                <AlertPrompt
                                    show={!!err.length}
                                    title="error_while_adding_appt"
                                    body={
                                        <div className="text-lg">{err}</div>
                                    }
                                    icon="alert-triangle bg-red-500"
                                    actions={[
                                        <button
                                            key={'Error'}
                                            className="px-7 py-2 text-white bg-primary rounded-md w-full"
                                            onClick={() => setErrMsg('')}
                                        >
                                            <Translation content_key="ok" />
                                        </button>,
                                    ]}
                                />
                            </div>
                        </Popup>
                    )}
                    {isSuccess && (
                        <>
                            <Popup
                                show={isSuccess}
                                popupAlign={{
                                    horizontal: 'right',
                                    vertical: 'top',
                                }}
                                animate={false}
                                popupClass={classNames(
                                    'bg-black h-screen transition ease-in-out duration-200 flex flex-row items-center justify-between',
                                    popup_anim_css
                                )}
                                style={{
                                    width: '100%',
                                }}
                                className="z-[102]"
                            >
                                <div className="flex-1">
                                    <AlertPrompt
                                        show={isSuccess}
                                        title="appointment_added"
                                        body=""
                                        icon="check bg-green-500"
                                        actions={[
                                            <button
                                                key={'Success'}
                                                className="px-7 py-2 text-white bg-primary rounded-md w-full"
                                                onClick={() => {
                                                    props?.onClose &&
                                                        props?.onClose()
                                                    if (
                                                        router.pathname.includes(
                                                            'calendar'
                                                        )
                                                    )
                                                        window.location.reload()
                                                }}
                                            >
                                                <Translation content_key="ok" />
                                            </button>,
                                        ]}
                                    />
                                </div>
                            </Popup>
                        </>
                    )}
                    {displayAddAttendeeModal && (
                        <div className="p-4">
                            <AddAttendeeModal />
                        </div>
                    )}

                    {displayNewClientForm && (
                        <div className="p-4">
                            <Popup
                                show={displayNewClientForm}
                                popupAlign={{
                                    horizontal: 'right',
                                    vertical: 'top',
                                }}
                                animate={false}
                                popupClass={classNames(
                                    'bg-black h-screen transition ease-in-out duration-200 flex flex-row items-center justify-between',
                                    popup_anim_css
                                )}
                                style={{
                                    width: '100%',
                                }}
                                className="z-[102]"
                            >
                                <div className="flex-1">
                                    <AlertPrompt
                                        show={displayNewClientForm}
                                        title="add_new_client_basic"
                                        body=""
                                        icon={undefined}
                                        actions={[
                                            <div key={'new'}>
                                                <AddNewClient />
                                            </div>,
                                        ]}
                                    />
                                </div>
                            </Popup>
                        </div>
                    )}

                    <div className="bg-gray-50 p-4">
                        {!displayAddAttendeeModal &&
                            renderAttendeesSection()}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AppointmentDrawer
